import React from "react";
import { useShallow } from "zustand/react/shallow";
import DraggablePanel from "./DraggablePanel";
import {
  useActuators,
  useBodies,
  useCameras,
  useEqualities,
  useJoints,
  useMeshes,
  useSceneActions,
  useSites,
  useTendons,
  useWorldBodies,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import SceneInventorySection from "./scene-inventory/SceneInventorySection";
import { useSceneInventoryQuickAdd } from "./scene-inventory/useSceneInventoryQuickAdd";
import { useSceneInventoryRows } from "./scene-inventory/useSceneInventoryRows";
import { useSceneInventorySelection } from "./scene-inventory/useSceneInventorySelection";
import { useSceneInventoryVisibility } from "./scene-inventory/useSceneInventoryVisibility";

const SceneInventoryPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const joints = useJoints();
  const actuators = useActuators();
  const meshes = useMeshes();
  const sites = useSites();
  const equalities = useEqualities();
  const tendons = useTendons();
  const cameras = useCameras();
  const sceneActions = useSceneActions();
  const isPhysicsRunning = usePhysicsStore((state) => state.isRunning);
  const [expandedRows, setExpandedRows] = React.useState({});

  const selectedTargets = useSelectionStore(
    useShallow((state) => ({
      body: state.selectedBody,
      joint: state.selectedJoint,
      actuator: state.selectedActuator,
      mesh: state.selectedMesh,
      site: state.selectedSite,
      geom: state.selectedGeom,
      inertial: state.selectedInertial,
      equality: state.selectedEquality,
      tendon: state.selectedTendon,
      camera: state.selectedCamera,
    }))
  );
  const selectedBodies = useSelectionStore((state) => state.selectedBodies);
  const toggleBodyInSelection = useSelectionStore((state) => state.toggleBodyInSelection);

  const { rows, sceneOrder, selectedMeshCount, selectedMeshIds } = useSceneInventoryRows({
    actuators,
    bodies,
    cameras,
    equalities,
    joints,
    meshes,
    sites,
    tendons,
    worldBodies,
  });

  const {
    getItemHidden,
    getLabelToggleActive,
    getTypeVisible,
    toggleItemVisibility,
    toggleTypeLabels,
    toggleTypeVisibility,
  } = useSceneInventoryVisibility();

  const { getQuickAddDisabledReason, handleQuickAdd } = useSceneInventoryQuickAdd({
    bodies,
    cameras,
    equalities,
    joints,
    sceneActions,
    sceneOrder,
    selectedTargets,
    tendons,
    isPhysicsRunning,
  });

  const {
    clearMultiSelection,
    getActiveTargetId,
    handleTargetRowKeyDown,
    openTarget,
    selectAllTargets,
    toggleMultiSelection,
  } = useSceneInventorySelection({
    actuators,
    selectedMeshIds,
    selectedTargets,
    toggleBodyInSelection,
  });

  const toggleExpandedRow = React.useCallback((rowKey) => {
    setExpandedRows((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  }, []);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Scene Inventory"
      titleIcon={titleIcon}
      initialSize={{ width: 370, height: 480 }}
      minWidth={330}
      minHeight={300}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex-1 overflow-auto p-3 text-gray-200">
        <div className="space-y-2">
          {rows.map((row) => {
            const isExpanded = !!expandedRows[row.key];
            const activeTargetId = getActiveTargetId(row.key);
            const activeTarget = activeTargetId
              ? row.targets.find((target) => target.id === activeTargetId)
              : null;
            const hiddenCount = row.hasVisibility
              ? row.targets.filter((target) => getItemHidden(row.key, target.id)).length
              : 0;
            const labelToggleActive = getLabelToggleActive(row.key);
            const typeVisible = getTypeVisible(row.key);
            const quickAddDisabledReason = getQuickAddDisabledReason(row.key);
            const selectedCount =
              row.key === "body"
                ? selectedBodies.length
                : row.key === "mesh"
                  ? selectedMeshCount
                  : 0;

            return (
              <SceneInventorySection
                key={row.key}
                row={row}
                isExpanded={isExpanded}
                activeTargetId={activeTargetId}
                activeTarget={activeTarget}
                hiddenCount={hiddenCount}
                labelToggleActive={labelToggleActive}
                typeVisible={typeVisible}
                quickAddDisabledReason={quickAddDisabledReason}
                selectedCount={selectedCount}
                selectedBodies={selectedBodies}
                selectedMeshIds={selectedMeshIds}
                getItemHidden={getItemHidden}
                onToggleExpanded={toggleExpandedRow}
                onQuickAdd={handleQuickAdd}
                onToggleTypeVisibility={toggleTypeVisibility}
                onToggleTypeLabels={toggleTypeLabels}
                onToggleItemVisibility={toggleItemVisibility}
                onClearMultiSelection={clearMultiSelection}
                onSelectAllTargets={selectAllTargets}
                onToggleMultiSelection={toggleMultiSelection}
                onOpenTarget={openTarget}
                onTargetRowKeyDown={handleTargetRowKeyDown}
              />
            );
          })}
        </div>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(SceneInventoryPanel);
